import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import dateFormater, { parseDateString } from "@/helperFuntions/dateFormater";
import { useUser } from "@/hooks/useUser";
import useOrganisationDetailsQuery from "@/api/query/useOrganisationDetailsQuery";
import { getCurrentUserOrganisation } from "@/utils/roleUtils";
import { OrgListOfNonWorkingDaysEnum } from "@backend/src/schemas/organisationSchema";
import { useTranslation } from "react-i18next";

export interface DatePickerDisabledRules {
  /** Explicit list of weekday numbers (0 = Sun, 1 = Mon, ..., 6 = Sat) to disable */
  nonWorkingDays?: number[];
  /** Dates strictly before this date will be disabled */
  before?: Date;
  /** Dates strictly after this date will be disabled */
  after?: Date;
  /** List of specific holiday Dates to disable */
  holidays?: Date[];
  /** Custom matcher function: return true to disable the date */
  isDateDisabled?: (date: Date) => boolean;
}

export interface CommonDatePickerProps {
  /** Currently selected date */
  value?: Date | string | null;
  /** Change callback */
  onChange?: (date: Date | undefined) => void;
  /** Placeholder text when no date is selected */
  placeholder?: string;
  /** Disabled state for the entire picker */
  disabled?: boolean;
  /** Whether to automatically apply the current organization's non-working days and holidays (default: true) */
  applyOrgRules?: boolean;
  /** Specific organisationId to use if different from current active organisation */
  organisationId?: string;
  /** Additional custom disabled rules */
  disabledRules?: DatePickerDisabledRules;
  /** Extra DayPicker matchers or matcher array to disable */
  customDisabled?: any;
  /** Year range for dropdown navigation */
  fromYear?: number;
  toYear?: number;
  /** Custom trigger className */
  className?: string;
  /** Custom popover content className */
  popoverClassName?: string;
  /** Custom format string or formatter function */
  displayFormat?: string | ((date: Date) => string);
  /** Custom icon */
  icon?: React.ReactNode;
  /** Custom render trigger */
  trigger?: React.ReactNode;
}

export const mapOrgDayToNumber = (day: OrgListOfNonWorkingDaysEnum | string): number => {
  switch (day) {
    case OrgListOfNonWorkingDaysEnum.SUN:
    case "SUN":
      return 0;
    case OrgListOfNonWorkingDaysEnum.MON:
    case "MON":
      return 1;
    case OrgListOfNonWorkingDaysEnum.TUE:
    case "TUE":
      return 2;
    case OrgListOfNonWorkingDaysEnum.WED:
    case "WED":
      return 3;
    case OrgListOfNonWorkingDaysEnum.THU:
    case "THU":
      return 4;
    case OrgListOfNonWorkingDaysEnum.FRI:
    case "FRI":
      return 5;
    case OrgListOfNonWorkingDaysEnum.SAT:
    case "SAT":
      return 6;
    default:
      return 0;
  }
};

export const CommonDatePicker: React.FC<CommonDatePickerProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  applyOrgRules = true,
  organisationId,
  disabledRules,
  customDisabled,
  fromYear = 2020,
  toYear = 2050,
  className,
  popoverClassName,
  displayFormat,
  icon,
  trigger,
}) => {
  const { t } = useTranslation();
  const effectivePlaceholder = placeholder || t("select_date");
  const [open, setOpen] = React.useState(false);
  const { user } = useUser();

  const selectedDate = React.useMemo(() => {
    if (!value) return undefined;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? undefined : value;
    }
    return parseDateString(String(value));
  }, [value]);

  const [currentMonth, setCurrentMonth] = React.useState<Date | undefined>(
    selectedDate || new Date()
  );

  React.useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(selectedDate);
    }
  }, [selectedDate, open]);

  const effectiveOrgId = React.useMemo(() => {
    if (organisationId) return organisationId;
    return getCurrentUserOrganisation(user)?.organisationId || localStorage.getItem("organisation-id") || "";
  }, [organisationId, user]);

  const { data: orgDetailResponse } = useOrganisationDetailsQuery(
    effectiveOrgId,
    applyOrgRules && Boolean(effectiveOrgId)
  );
  const liveOrg = orgDetailResponse?.data?.data;

  // Extract org non-working days and holidays
  const { orgNonWorkingDayNumbers, orgHolidayDates } = React.useMemo(() => {
    if (!applyOrgRules) {
      return { orgNonWorkingDayNumbers: [], orgHolidayDates: [] };
    }

    const currentOrg = getCurrentUserOrganisation(user, organisationId || effectiveOrgId);
    const fallbackOrg = currentOrg?.organisation;

    const nonWorkingDays: (OrgListOfNonWorkingDaysEnum | string)[] =
      (liveOrg?.nonWorkingDays && liveOrg.nonWorkingDays.length > 0)
        ? liveOrg.nonWorkingDays
        : (fallbackOrg?.nonWorkingDays && fallbackOrg.nonWorkingDays.length > 0)
        ? fallbackOrg.nonWorkingDays
        : [];

    const orgHolidays =
      (liveOrg?.orgHolidays && liveOrg.orgHolidays.length > 0)
        ? liveOrg.orgHolidays
        : (fallbackOrg?.orgHolidays && fallbackOrg.orgHolidays.length > 0)
        ? fallbackOrg.orgHolidays
        : [];

    const dayNumbers = nonWorkingDays.map((d) => mapOrgDayToNumber(d));
    const holidayDates = orgHolidays
      .map((h) => new Date(h.holidayStartDate))
      .filter((d) => !isNaN(d.getTime()));

    return {
      orgNonWorkingDayNumbers: dayNumbers,
      orgHolidayDates: holidayDates,
    };
  }, [applyOrgRules, liveOrg, user, organisationId, effectiveOrgId]);

  // Combine matchers for react-day-picker
  const matchers = React.useMemo(() => {
    const list: any[] = [];

    // 1. Non-working days (from org rules + custom)
    const combinedNonWorkingDays = Array.from(
      new Set([
        ...orgNonWorkingDayNumbers,
        ...(disabledRules?.nonWorkingDays || []),
      ])
    );
    if (combinedNonWorkingDays.length > 0) {
      list.push({ dayOfWeek: combinedNonWorkingDays });
    }

    // 2. Holidays (from org rules + custom)
    const allHolidays = [
      ...orgHolidayDates,
      ...(disabledRules?.holidays || []),
    ];
    if (allHolidays.length > 0) {
      list.push(...allHolidays);
    }

    // 3. Before date limit
    if (disabledRules?.before) {
      list.push({ before: new Date(disabledRules.before) });
    }

    // 4. After date limit
    if (disabledRules?.after) {
      list.push({ after: new Date(disabledRules.after) });
    }

    // 5. Custom matcher function
    if (disabledRules?.isDateDisabled) {
      list.push((date: Date) => disabledRules.isDateDisabled!(date));
    }

    // 6. Additional custom matchers passed directly
    if (customDisabled) {
      if (Array.isArray(customDisabled)) {
        list.push(...customDisabled);
      } else {
        list.push(customDisabled);
      }
    }

    return list;
  }, [
    orgNonWorkingDayNumbers,
    orgHolidayDates,
    disabledRules,
    customDisabled,
  ]);

  const formattedDisplay = React.useMemo(() => {
    if (!selectedDate) return effectivePlaceholder;
    if (typeof displayFormat === "function") {
      return displayFormat(selectedDate);
    }
    if (typeof displayFormat === "string") {
      return format(selectedDate, displayFormat);
    }
    return dateFormater(selectedDate);
  }, [selectedDate, effectivePlaceholder, displayFormat]);

  const handleSelect = (date: Date | undefined) => {
    if (onChange) {
      onChange(date);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {trigger ? (
          trigger
        ) : (
          <Button
            type="button"
            variant="none"
            disabled={disabled}
            className={cn(
              "flex items-center justify-between h-9 px-3 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg text-xs font-medium text-slate-800 transition-all shadow-2xs",
              disabled && "opacity-50 cursor-not-allowed hover:bg-white border-slate-200",
              className
            )}
          >
            <span className={cn(!selectedDate && "text-slate-400 font-normal")}>
              {formattedDisplay}
            </span>
            {icon || <CalendarIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "w-auto p-0 z-[9999] shadow-xl border border-slate-200 rounded-xl bg-white",
          popoverClassName
        )}
        align="start"
      >
        <Calendar
          mode="single"
          fromYear={fromYear}
          toYear={toYear}
          captionLayout="dropdown-buttons"
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          selected={selectedDate}
          onSelect={handleSelect}
          disabled={matchers}
          className="rounded-xl"
        />
      </PopoverContent>
    </Popover>
  );
};

export default CommonDatePicker;
